<template>
  <div class="picker" ref="root">
    <div class="box" :class="{ focused }" @click="$refs.input.focus()">
      <span class="chip" v-for="u in modelValue" :key="u.id">
        <Avatar :name="fullName(u)" :image="u.image" :size="20" />
        {{ fullName(u) }}
        <button type="button" @click.stop="remove(u)" :aria-label="`Remove ${fullName(u)}`">
          <i class="ri-close-line"></i>
        </button>
      </span>
      <input
        ref="input"
        v-model="query"
        :placeholder="modelValue.length ? 'Add more…' : placeholder"
        @input="search"
        @focus="focused = true"
        @blur="focused = false"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="pick(results[active])"
        @keydown.backspace="backspace"
        autocomplete="off"
      />
      <span class="spinner" v-if="loading"></span>
    </div>
    <Transition name="pop">
      <div class="results menu scroll-thin" v-if="open" ref="results">
        <button
          type="button"
          class="menu-item"
          v-for="(u, i) in results"
          :key="u.id"
          :class="{ hover: i === active }"
          @mousedown.prevent="pick(u)"
          @mouseenter="active = i"
        >
          <Avatar :name="fullName(u)" :image="u.image" :size="32" />
          <span class="who">
            <strong>{{ fullName(u) }}</strong>
            <small>{{ u.email }}</small>
          </span>
          <span class="badge">{{ u.role }}</span>
        </button>
        <div class="none" v-if="!results.length && !loading">
          No one in your school matches “{{ query }}”
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import Avatar from "./Avatar.vue";
import { people, errorMessage } from "@/api";

// Search the school's people and pick several. v-model is the picked users
// (id, first_name, last_name, email, image, role).
export default {
  name: "PeoplePicker",
  components: { Avatar },
  emits: ["update:modelValue"],
  props: {
    modelValue: { type: Array, default: () => [] },
    exclude: { type: Array, default: () => [] }, // ids already on the meeting
    placeholder: { type: String, default: "Search by name or email" },
  },
  data() {
    return { query: "", results: [], loading: false, focused: false, active: 0, timer: null, asked: "" };
  },
  watch: {
    // Inside a scrolling dialog the list could open out of sight.
    open(now) {
      if (now) this.$nextTick(() => this.$refs.results && this.$refs.results.scrollIntoView({ block: "nearest", behavior: "smooth" }));
    },
  },
  computed: {
    open() {
      return this.focused && this.query.trim().length > 0 && (this.results.length > 0 || this.asked === this.query.trim());
    },
  },
  methods: {
    fullName(u) {
      return `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email;
    },
    search() {
      clearTimeout(this.timer);
      const q = this.query.trim();
      if (!q) {
        this.results = [];
        return;
      }
      this.loading = true;
      this.timer = setTimeout(async () => {
        try {
          const response = await people.search(q);
          const taken = new Set([...this.exclude, ...this.modelValue.map((u) => u.id)]);
          this.results = (response.data.data.users.docs || []).filter((u) => !taken.has(u.id));
          this.active = 0;
          this.asked = q;
        } catch (e) {
          this.$toast.error(errorMessage(e), { position: "top" });
        } finally {
          this.loading = false;
        }
      }, 250);
    },
    move(step) {
      if (!this.results.length) return;
      this.active = (this.active + step + this.results.length) % this.results.length;
    },
    pick(u) {
      if (!u) return;
      this.$emit("update:modelValue", [...this.modelValue, u]);
      this.query = "";
      this.results = [];
    },
    remove(u) {
      this.$emit("update:modelValue", this.modelValue.filter((x) => x.id !== u.id));
    },
    backspace() {
      if (!this.query && this.modelValue.length) {
        this.remove(this.modelValue[this.modelValue.length - 1]);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.picker {
  position: relative;
}

.box {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 5px 0.6rem;
  border: 1px solid var(--line-2);
  border-radius: var(--radius);
  background: var(--surface);
  cursor: text;
  transition: border-color 0.15s, box-shadow 0.15s;

  &.focused {
    border-color: var(--brand);
    box-shadow: var(--ring);
  }

  input {
    flex: 1;
    min-width: 140px;
    height: 32px;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.95rem;

    &::placeholder {
      color: var(--ink-3);
    }
  }

  .spinner {
    width: 16px;
    height: 16px;
    color: var(--ink-3);
  }
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 4px 0 4px;
  border-radius: 999px;
  background: var(--brand-50);
  border: 1px solid var(--brand-100);
  font-size: 0.85rem;
  font-weight: 500;

  button {
    display: inline-flex;
    width: 22px;
    height: 22px;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--ink-3);
    cursor: pointer;

    &:hover {
      background: var(--brand-100);
      color: var(--ink);
    }
  }
}

// The list sits under the box in the flow, not floating over it: a dialog's
// scroll area would clip a floating one.
.results {
  position: static;
  margin-top: 6px;
  box-shadow: var(--shadow);
  max-height: 260px;
  overflow-y: auto;

  .menu-item.hover {
    background: var(--canvas);
  }

  .who {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;

    strong {
      font-size: 0.9rem;
      font-weight: 600;
    }

    small {
      font-size: 0.78rem;
      color: var(--ink-3);
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .none {
    padding: 0.8rem;
    font-size: 0.85rem;
    color: var(--ink-3);
    text-align: center;
  }
}
</style>
